import { useState } from "react";
import { api, type AuditRow } from "../api";
import { useApiData } from "../useApi";
import { PageState, formatTime } from "../components";

export function Audit({ onSignedOut }: { onSignedOut: () => void }) {
  const { data, error, loading, reload } = useApiData(() => api.audit(200), onSignedOut);
  const [filter, setFilter] = useState("");

  const events = (data?.events ?? []).filter((event) => {
    if (!filter) return true;
    const needle = filter.toLowerCase();
    return (
      event.actor.toLowerCase().includes(needle) ||
      event.action.toLowerCase().includes(needle) ||
      event.resource.toLowerCase().includes(needle) ||
      event.reason.toLowerCase().includes(needle)
    );
  });

  return (
    <>
      <h1>Audit and Change Log</h1>
      <p className="subtitle">
        Who changed what, when, and why — for people and for the EA alike.{" "}
        <button className="link" onClick={reload}>
          Refresh
        </button>
      </p>

      <div className="card">
        <label style={{ marginBottom: 0 }}>
          Filter
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="actor, action, resource or reason"
          />
        </label>
      </div>

      <PageState
        loading={loading}
        error={error}
        empty={events.length === 0}
        emptyMessage={filter ? "No events match that filter." : "No audit events yet."}
      >
        <div className="card">
          <table>
            <thead>
              <tr>
                <th>Time (UTC)</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Resource</th>
                <th>Reason</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <Row key={event.event_id} event={event} />
              ))}
            </tbody>
          </table>
        </div>
      </PageState>
    </>
  );
}

function Row({ event }: { event: AuditRow }) {
  const hasDetail = event.before !== null || event.after !== null;
  // ea:* is the MT5 terminal, user:* a person, ops:* a command-line script.
  // Keeping the prefix visible is the point — they must never be confusable.
  const actorKind = event.actor.split(":")[0];

  return (
    <tr>
      <td className="dim mono">{formatTime(event.created_at)}</td>
      <td>
        <span className="badge">{actorKind}</span> {event.actor.split(":").slice(1).join(":")}
      </td>
      <td className="mono">{event.action}</td>
      <td className="dim">{event.resource}</td>
      <td>{event.reason}</td>
      <td>
        {hasDetail && (
          <details>
            <summary>detail</summary>
            <pre>{JSON.stringify({ before: event.before, after: event.after }, null, 2)}</pre>
          </details>
        )}
      </td>
    </tr>
  );
}
